import React, { useState, useEffect } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CancellationModal from "./CancellationModal";
import ReactivationModal from "./ReactivationModal";
import ServiceCard from "./ServiceCard";
import UpgradePlanModal from "./UpgradePlanModal";
import BulkQueriesCard from "./BulkQueriesCard";
import { getBulkQueries, toggleBulkQueries } from "./bulkQueriesApi";
import { generateTraceId } from "../../../helpers";
import {
  JUSZAP_STATUS_MAP,
  JUSMAIL_STATUS_MAP,
  JUSCONT_STATUS_MAP,
  JUSCONT_PLAN_LABELS,
  JUSCONT_ORIGIN_LABELS,
  JUSCONT_BILLING_CYCLE_LABELS
} from "./constants";
import { config } from "../../../../config/envs";
import BlockUi from "react-block-ui";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import { JdsButton } from "../../../components/jds/JdsButton";
import { JdsTag } from "../../../components/jds/JdsTag";
import { ClienteModal, Field } from "./clienteUi";
import "../../../components/jds/jds.css";
import "../clienteDados.css";

const AI_PROVIDERS = [
  { value: "OPENAI", label: "OpenAI", svg: "/media/svg/icons/ai/chatgpt_logo_chatgpt_logo_square_green_gpt_ia_openai_icon_264976.svg" },
  { value: "GEMINI", label: "Gemini", svg: "/media/svg/icons/ai/Google_Gemini_icon_2025.svg" },
];

function ProviderLogo({ provider, size = 20 }) {
  if (!provider) return null;
  return (
    <img
      src={toAbsoluteUrl(provider.svg)}
      alt={provider.label}
      style={{ width: size, height: size, objectFit: "contain" }}
    />
  );
}

function WhatsappField({ connected }) {
  return (
    <Field label="WhatsApp">
      <JdsTag
        dot
        color={connected ? "success" : "error"}
        label={connected ? "Conectado" : "Desconectado"}
      />
    </Field>
  );
}

function JusGptCard({ data, isLoading, onChangeAi }) {
  const provider = AI_PROVIDERS.find(p => p.value === data?.provider) || AI_PROVIDERS[0];

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <div className="col-xl-6 col-lg-6 col-md-12 mb-5">
      <BlockUi tag="div" blocking={isLoading} style={{ height: "100%" }}>
        <div className="jds-card cd-product">
          <div className="cd-product-head">
            <span className="cd-product-logo">
              <ProviderLogo provider={provider} size={24} />
            </span>
            <h2 className="jds-title-md">JusGPT</h2>
          </div>

          <Field label="Modelo de IA">
            <ProviderLogo provider={provider} size={18} />
            <span>{provider.label}</span>
          </Field>

          <Field label="Alterado manualmente">
            <JdsTag
              color={data?.manual_override ? "info" : "neutral"}
              label={data?.manual_override ? "Sim" : "Não"}
            />
          </Field>

          <Field label="Atualizado em">{formatDate(data?.updated_at)}</Field>

          <Field label="Criado em">{formatDate(data?.created_at)}</Field>

          <div className="cd-actions cd-actions--end mt-6">
            <JdsButton variant="outlined" onClick={onChangeAi}>
              Alterar IA
            </JdsButton>
          </div>
        </div>
      </BlockUi>
    </div>
  );
}

function ChangeAiModal({ currentProvider, onConfirm, onClose }) {
  const [selected, setSelected] = useState(currentProvider || "OPENAI");

  return (
    <ClienteModal
      title="Alterar modelo de IA"
      description="Selecione o modelo de IA que será utilizado pelo JusGPT."
      onClose={onClose}
      actions={
        <>
          <JdsButton variant="outlined" color="neutral" onClick={onClose}>
            Voltar
          </JdsButton>
          <JdsButton onClick={() => onConfirm(selected)}>Confirmar</JdsButton>
        </>
      }
    >
      {AI_PROVIDERS.map((provider) => (
        <label key={provider.value} className="cd-check">
          <input
            type="radio"
            name="ai_provider"
            value={provider.value}
            checked={selected === provider.value}
            onChange={() => setSelected(provider.value)}
          />
          <ProviderLogo provider={provider} size={20} />
          <span>{provider.label}</span>
        </label>
      ))}
    </ClienteModal>
  );
}

// TODO: remover mock após testes - força o botão de reativação sempre habilitado
const MOCK_REACTIVATION = false;

// Limite do campo reason no DELETE /api/juscont/cancel (juspage-backend).
const JUSCONT_CANCEL_REASON_MAX_LENGTH = 255;

export default function Produtos({ cliente_id, user_id }) {
  const dispatch = useDispatch();
  const credit_cards = useSelector(state => state.app.cards);
  const is_loading_cards = useSelector(state => state.app.is_loading_cards);
  const clientSubscription = useSelector(state => state.app.client_subscription);

  const [modalConfig, setModalConfig] = useState(null);
  const [reactivationModalConfig, setReactivationModalConfig] = useState(null);
  const [showAiModal, setShowAiModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [jusGptData, setJusGptData] = useState(null);

  const [juszapData, setJuszapData] = useState(null);
  const [jusmailData, setJusmailData] = useState(null);
  const [juscontData, setJuscontData] = useState(null);
  const [jusZapHasCustomPlan, setJusZapHasCustomPlan] = useState(null);
  const [isLoadingJusZap, setLoadingJusZap] = useState(true);
  const [isLoadingJusMail, setIsLoadingJusMail] = useState(true);
  const [isLoadingJusCont, setIsLoadingJusCont] = useState(true);
  const [isLoadingJusGpt, setIsLoadingJusGpt] = useState(true);

  const [bulkQuery, setBulkQuery] = useState(null);
  const [isLoadingBulkQuery, setIsLoadingBulkQuery] = useState(true);


  async function loadJusZapData(uid) {
    try {
      setLoadingJusZap(true);
      const response = await axios.get(
        `${config.juszap.apiUrl}/juszap-user/${uid}`,
        { headers: { 'x-trace-id': generateTraceId() } },
      );
      if (response.data?.data?.success === false) {
        setJuszapData({ _notFound: true });
        return;
      }
      setJuszapData(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar dados do JusZap:', error);
      if (error.response?.data?.code === 'E300') {
        setJuszapData({ _notFound: true });
      } else {
        setJuszapData({ _error: true });
      }
    } finally {
      setLoadingJusZap(false);
    }
  }

  async function loadJusMailData(uid) {
    try {
      setIsLoadingJusMail(true);
      const response = await axios.get(
        `${config.juspage.apiUrl}/jusmail/${uid}`,
        { headers: { 'x-trace-id': generateTraceId() } },
      );
      if (response.data?.data?.success === false) {
        setJusmailData({ _notFound: true });
        return;
      }
      if (response.data?.code === 'E300') {
        setJusmailData({ _notFound: true });
        return;
      }

      setJusmailData(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar dados do JusMail:', error);
      if (error.response?.data?.code === 'E300') {
        setJusmailData({ _notFound: true });
      } else {
        setJusmailData({ _error: true });
      }
    } finally {
      setIsLoadingJusMail(false);
    }
  }

  async function loadJusContData(uid) {
    // GET /api/juscont/status/:user_id (juspage-backend) — leitura administrativa, exige
    // admin. Mesmo payload do GET /api/juscont/status, mas com o dado local (sem sync
    // com o addon-manager). 404 = cliente sem contratação do JusCont.
    try {
      setIsLoadingJusCont(true);
      const response = await axios.get(
        `${config.juspage.apiUrl}/juscont/status/${uid}`,
        {
          headers: { 'x-trace-id': generateTraceId() },
        },
      );

      if (response.data?.code === 'E300' || response.data?.data?.contracted === false) {
        setJuscontData({ _notFound: true });
        return;
      }

      // Normaliza para o shape que o ServiceCard consome
      // (configuration.status / checkout.nextChargeDate / cancellation.cancelled_at).
      const status = response.data?.data ?? {};

      // Sem status não dá para saber se o cancelamento é permitido: trata como erro.
      if (!status.status) {
        console.error('Resposta do JusCont sem status:', response.data);
        setJuscontData({ _error: true });
        return;
      }

      setJuscontData({
        configuration: {
          user_id: uid,
          status: status.status,
          plan: status.plan,
          origin: status.origin,
          billing_cycle: status.billing_cycle,
          company_status: status.company_status,
        },
        checkout: { nextChargeDate: status.next_charge_date },
        cancellation: status.canceled_at ? { cancelled_at: status.canceled_at } : null,
      });
    } catch (error) {
      console.error('Erro ao carregar dados do JusCont:', error);
      if (error.response?.status === 404 || error.response?.data?.code === 'E300') {
        setJuscontData({ _notFound: true });
      } else {
        setJuscontData({ _error: true });
      }
    } finally {
      setIsLoadingJusCont(false);
    }
  }

  async function loadJusGPTData(user_id) {
    try {
      setIsLoadingJusGpt(true);
      const response = await axios.get(
        `${config.jusgpt.apiUrl}/api/ai-provider-overrides?user_id=${user_id}`
      );
      setJusGptData(response.data);
    } catch (error) {
      console.error('[JusGPT] Erro ao carregar dados:', error);
    } finally {
      setIsLoadingJusGpt(false);
    }
  }

  async function loadJusZapHasCustomPlan(uid) {
    const data = await getJusZapHasCustomPlan(uid);
    setJusZapHasCustomPlan(data);
  }

  // GET é a fonte da verdade do label da Consulta em Lote
  async function loadBulkQueries(uid) {
    try {
      setIsLoadingBulkQuery(true);
      const data = await getBulkQueries({ clientId: uid });
      setBulkQuery(data);
    } catch (error) {
      console.error("[BulkQueries] Erro ao carregar status:", error);
      toast.error("Erro ao carregar o status da Consulta em Lote.");
    } finally {
      setIsLoadingBulkQuery(false);
    }
  }

  useEffect(() => {
    if (user_id) {
      loadJusZapData(user_id);
      loadJusMailData(user_id);
      loadJusContData(user_id);
      loadJusGPTData(user_id);
      loadJusZapHasCustomPlan(user_id);
      loadBulkQueries(cliente_id);
    } else {
      setLoadingJusZap(false);
      setIsLoadingJusMail(false);
      setIsLoadingJusCont(false);
      setIsLoadingJusGpt(false);
      setIsLoadingBulkQuery(false);
    }
  }, [user_id]);

  const mainUserJusZapId = juszapData?.configuration?.main_user_id;
  const mainUserJusmailId = jusmailData?.configuration?.user_id;
  const isJusZapCancellable = Boolean(mainUserJusZapId) && juszapData?.configuration?.status !== "CANCELLED";
  const isJusMailCancellable = Boolean(mainUserJusmailId) && jusmailData?.configuration?.status !== "CANCELLED";

  // JusCont: o cancelamento usa o user_id da Jusfy (prop desta tela), que é o que
  // o DELETE /api/juscont/cancel do juspage-backend espera no body.
  const juscontStatus = juscontData?.configuration?.status;
  const isJusContCancellable =
    Boolean(user_id) &&
    Boolean(juscontData) &&
    !juscontData?._notFound &&
    !juscontData?._error &&
    !juscontData?.cancellation?.cancelled_at &&
    juscontStatus !== "CANCELLED";
  const hasCustomPlan = jusZapHasCustomPlan?.hasCustomPlan === true;
  const isJusZapUpgradable =
    Boolean(mainUserJusZapId) &&
    juszapData?.configuration?.status !== "CANCELLED" &&
    jusZapHasCustomPlan !== null &&
    !hasCustomPlan;

  const jusmailStatus = jusmailData?.configuration?.status;
  const jusmailNextChargeDate = jusmailData?.checkout?.nextChargeDate
    ? new Date(jusmailData.checkout.nextChargeDate)
    : null;
  const isJusMailExpired =
    Boolean(jusmailNextChargeDate) &&
    !isNaN(jusmailNextChargeDate.getTime()) &&
    new Date() > jusmailNextChargeDate;

  const isJusMailReactivatable = MOCK_REACTIVATION || (
    Boolean(jusmailData) &&
    Boolean(mainUserJusmailId) &&
    !jusmailData?._notFound &&
    !jusmailData?._error &&
    ["RENEWING", "EXPIRED", "READY"].includes(jusmailStatus) &&
    isJusMailExpired
  );

  const handleJusZapAction = async (data) => {
    try {
      await axios.delete(
        `${config.juszap.apiUrl}/juszap-configuration/cancel`,
        {
          data: {
            client_id: cliente_id,
            user_id: mainUserJusZapId,
            id: data.id,
            cancellationReason: data.cancellationReason
          },
          headers: {
            "x-trace-id": generateTraceId()
          }
        }
      );

      toast.success("Cancelamento da IA WhatsApp realizado com sucesso!");
      setModalConfig(null);
    } catch (error) {
      toast.error("Erro ao cancelar a IA WhatsApp.");
      console.error(error);
    }
  };

  async function getJusZapUpgradePreview(uid, newPlanValue = 99) {
    try {
      const response = await axios.get(
        `${config.juszap.apiUrl}/juszap-checkout/upgrade/preview`,
        {
          params: { user_id: uid, newPlanValue },
          headers: { 'x-trace-id': generateTraceId() }
        }
      );
      return response.data?.data;
    } catch (error) {
      console.error('Erro ao consultar preview de upgrade JusZap:', error);
      toast.error('Erro ao consultar preview de upgrade da IA WhatsApp.');
      throw error;
    }
  }

  async function postJusZapUpgradePro({ uid, planValue = 99, cardId, paymentMethod = 'CREDIT_CARD' }) {
    const response = await axios.post(
      `${config.juszap.apiUrl}/juszap-checkout/upgrade/pro`,
      {
        user_id: uid,
        plan_value: planValue,
        payment_method: paymentMethod,
        card_id: cardId
      },
      { headers: { 'x-trace-id': generateTraceId() } }
    );
    return response.data?.data;
  }

  async function getJusZapHasCustomPlan(uid) {
    try {
      const response = await axios.get(
        `${config.juszap.apiUrl}/juszap-checkout/has-custom-plan`,
        {
          params: { user_id: uid },
          headers: { 'x-trace-id': generateTraceId() }
        }
      );
      return response.data?.data;
    } catch (error) {
      console.error('Erro ao verificar plano custom do JusZap:', error);
      return null;
    }
  }

  const handleUpgradeJusZap = async ({ cardId, planValue = 99 }) => {
    if (!mainUserJusZapId) {
      toast.error("Não foi possível identificar o usuário do JusZap para upgrade.");
      throw new Error("missing user_id");
    }
    if (!cardId) {
      toast.error("Selecione um cartão para realizar o upgrade.");
      throw new Error("missing card_id");
    }
    try {
      await postJusZapUpgradePro({
        uid: mainUserJusZapId,
        planValue,
        cardId,
        paymentMethod: 'CREDIT_CARD'
      });
      await Promise.all([
        loadJusZapData(user_id),
        loadJusZapHasCustomPlan(user_id)
      ]);
      toast.success("Upgrade da IA WhatsApp realizado com sucesso!");
      setShowUpgradeModal(false);
    } catch (error) {
      console.error('Erro ao realizar upgrade JusZap:', error);
      toast.error('Erro ao realizar upgrade da IA WhatsApp.');
      throw error;
    }
  };

  const handleToggleBulkQueries = async (enabled) => {
    setIsLoadingBulkQuery(true);
    try {
      await toggleBulkQueries({ clientId: cliente_id, enabled });
      await loadBulkQueries(cliente_id); // recarrega — label só troca com o enabled do GET
      toast.success(enabled ? "Consulta em Lote habilitada!" : "Consulta em Lote desabilitada!");
    } catch (error) {
      console.error("[BulkQueries] Erro ao alterar status:", error);
      toast.error("Erro ao alterar o status da Consulta em Lote.");
      setIsLoadingBulkQuery(false);
    }
  };

  const handleChangeAiProvider = async (newProvider) => {
    try {
      await axios.post(
        `${config.jusgpt.apiUrl}/api/ai-provider-overrides/switch-provider`,
        { provider: newProvider, user_id: user_id }
      );
      setJusGptData(prev => ({ ...prev, provider: newProvider, updated_at: new Date().toISOString() }));
      setShowAiModal(false);
      toast.success("Modelo de IA alterado com sucesso!");
    } catch (error) {
      console.error("[JusGPT] Erro ao alterar provedor:", error);
      toast.error("Erro ao alterar o modelo de IA.");
    }
  };

  const handleJusMailAction = async (data) => {
    try {
      await axios.delete(
        `${config.juspage.apiUrl}/jusmail/cancel`,
        {
          data: {
            client_id: cliente_id,
            user_id: mainUserJusmailId,
            id: data.id,
            cancellationReason: data.cancellationReason
          },
          headers: {
            "x-trace-id": generateTraceId()
          }
        }
      );

      toast.success("JusMail cancelado com sucesso!");
      setModalConfig(null);
    } catch (error) {
      toast.error("Erro ao cancelar o JusMail.");
      console.error(error);
    }
  };

  // data = { id, cancellationReason } (vem do CancellationModal; id = defaultId = user_id)
  //
  // DELETE /api/juscont/cancel (juspage-backend) — exige admin autenticado.
  // Body: { user_id: number, reason: string (max 255), description?: string }.
  // Efeito imediato: status vira CANCELLED, renovação desligada no addon-manager
  // e snapshot gravado em juscont_cancellations. Idempotente.
  const handleJusContAction = async (data) => {
    try {
      await axios.delete(
        `${config.juspage.apiUrl}/juscont/cancel`,
        {
          data: {
            user_id: Number(data.id ?? user_id),
            reason: data.cancellationReason,
          },
          headers: {
            "x-trace-id": generateTraceId()
          }
        }
      );

      await loadJusContData(user_id);
      toast.success("JusCont cancelado com sucesso!");
      setModalConfig(null);
    } catch (error) {
      const message = error.response?.data?.message;
      toast.error(message ? `Erro ao cancelar o JusCont: ${message}` : "Erro ao cancelar o JusCont.");
      console.error(error);
    }
  };

  const handleReactivationCardAction = async (card) => {
    const cardId = card?.hash;

    if (!cardId) {
      toast.error("Selecione um cartão para renovar o JusMail.");
      return;
    }

    if (!mainUserJusmailId) {
      toast.error("Não foi possível identificar o usuário do JusMail para renovação.");
      return;
    }

    try {
      await axios.post(
        `${config.juspage.apiUrl}/jusmail/renew-jusmail`,
        {
          user_id: mainUserJusmailId,
          card_id: cardId,
        },
        {
          headers: {
            'x-trace-id': generateTraceId(),
          },
        }
      );

      await loadJusMailData(user_id);
      toast.success("JusMail renovado com sucesso!");
      setReactivationModalConfig(null);
    } catch (error) {
      toast.error("Erro ao renovar o JusMail.");
      console.error(error);
    }

  };

  const handleReactivationPixAction = async () => {
    if (!mainUserJusmailId) {
      toast.error("Não foi possível identificar o usuário do JusMail para renovação.");
      return;
    }

    if (!cliente_id) {
      toast.error("Não foi possível identificar o cliente para renovação.");
      return;
    }

    try {
      await axios.post(
        `${config.juspage.apiUrl}/jusmail/renew-pix`,
        {
          user_id: mainUserJusmailId,
          client_id: cliente_id,
        },
        {
          headers: {
            'x-trace-id': generateTraceId(),
          },
        }
      );

      await loadJusMailData(user_id);
      toast.success("JusMail renovado via PIX com sucesso!");
      setReactivationModalConfig(null);
    } catch (error) {
      toast.error("Erro ao renovar o JusMail via PIX.");
      console.error(error);
    }
  };

  return (
    <div>
      <div className="row mb-6">
        <div className="col-12">
          <h2 className="jds-title-md">Suporte ao cliente</h2>
          <p className="text-muted mb-0">
            Gerencie serviços e ações de suporte para o cliente.
          </p>
        </div>
      </div>

      <div className="row">
        <ServiceCard
          isLoading={isLoadingJusZap}
          data={juszapData}
          serviceName="IA WhatsApp"
          statusMap={JUSZAP_STATUS_MAP}
          onCancel={(userId) => setModalConfig({
            service: 'IA WhatsApp',
            onConfirm: handleJusZapAction,
            defaultId: userId
          })}
          isCancellable={isJusZapCancellable}
          isUpgradable={isJusZapUpgradable}
          onUpgrade={() => {
            dispatch({ type: 'LOAD_CARDS', payload: {
              client_id: cliente_id,
              useV2: !!clientSubscription?.external_id,
            }});
            setShowUpgradeModal(true);
          }}
          userId={mainUserJusZapId}
          headerFields={[juszapData?.configuration?.name]}
          extraFields={
            <>
              {juszapData?.configuration?.status !== "CANCELLED" && (
                <WhatsappField connected={juszapData?.configuration?.whatsapp_connected} />
              )}
              {juszapData?.cancellation?.cancelled_at && (
                <WhatsappField connected={juszapData?.configuration?.whatsapp_connected} />
              )}
              {hasCustomPlan && (
                <Field label="Plano">
                  <JdsTag color="brand" label="Plano personalizado ativo" />
                </Field>
              )}
            </>
          }
          renewalDateField={juszapData?.checkout?.expires_at}
        />

        <ServiceCard
          isReactivatable={isJusMailReactivatable}
          onReactivate={() => {
            dispatch({ type: 'LOAD_CARDS', payload: {
              client_id: cliente_id,
              useV2: !!clientSubscription?.external_id,
            }});
            setReactivationModalConfig({ service: 'JusMail' });
          }}
          isLoading={isLoadingJusMail}
          data={jusmailData}
          serviceName="JusMail"
          statusMap={JUSMAIL_STATUS_MAP}
          onCancel={(userId) => setModalConfig({
            service: 'JusMail',
            onConfirm: handleJusMailAction,
            defaultId: userId
          })}
          isCancellable={isJusMailCancellable}
          userId={mainUserJusmailId}
          headerFields={[
            `Dominio: ${jusmailData?.configuration?.domain_name ?? jusmailData?.cancellation?.domain_name ?? "-"}`,
            `Email: ${jusmailData?.configuration?.email ?? jusmailData?.cancellation?.email ?? "-"}`
          ]}
          renewalDateField={jusmailData?.checkout?.nextChargeDate}
          extraFields={
            <>
              {jusmailData?.cancellation?.cancelled_at && (
                <Field label="Data do cancelamento" error>
                  {new Date(jusmailData?.cancellation?.cancelled_at).toLocaleDateString("pt-BR")}
                </Field>
              )}
            </>
          }
        />

        <ServiceCard
          isLoading={isLoadingJusCont}
          data={juscontData}
          serviceName="JusCont"
          statusMap={JUSCONT_STATUS_MAP}
          onCancel={(userId) => setModalConfig({
            service: 'JusCont',
            onConfirm: handleJusContAction,
            defaultId: userId,
            reasonMaxLength: JUSCONT_CANCEL_REASON_MAX_LENGTH
          })}
          isCancellable={isJusContCancellable}
          userId={user_id}
          headerFields={[
            `Plano: ${JUSCONT_PLAN_LABELS[juscontData?.configuration?.plan] ?? juscontData?.configuration?.plan ?? "-"}`,
            `Origem: ${JUSCONT_ORIGIN_LABELS[juscontData?.configuration?.origin] ?? juscontData?.configuration?.origin ?? "-"}`
          ]}
          renewalDateField={juscontData?.checkout?.nextChargeDate}
          extraFields={
            <>
              {juscontData?.configuration?.billing_cycle && !juscontData?.cancellation?.cancelled_at && (
                <Field label="Ciclo de cobrança">
                  {JUSCONT_BILLING_CYCLE_LABELS[juscontData.configuration.billing_cycle] ?? juscontData.configuration.billing_cycle}
                </Field>
              )}
              {juscontData?.cancellation?.cancelled_at && (
                <Field label="Data do cancelamento" error>
                  {new Date(juscontData.cancellation.cancelled_at).toLocaleDateString("pt-BR")}
                </Field>
              )}
            </>
          }
        />

        <JusGptCard
          data={jusGptData}
          isLoading={isLoadingJusGpt}
          onChangeAi={() => setShowAiModal(true)}
        />

        <BulkQueriesCard
          product={bulkQuery}
          isLoading={isLoadingBulkQuery}
          onToggle={handleToggleBulkQueries}
        />
      </div>

      {modalConfig && (
        <CancellationModal
          setOpen={() => setModalConfig(null)}
          title={modalConfig.service}
          onConfirm={modalConfig.onConfirm}
          defaultId={modalConfig.defaultId}
          reasonMaxLength={modalConfig.reasonMaxLength}
        />
      )}

      {reactivationModalConfig && (
        <ReactivationModal
          show={Boolean(reactivationModalConfig)}
          onClose={() => setReactivationModalConfig(null)}
          onConfirm={async (card) => {
            await handleReactivationCardAction(card);
          }}
          onConfirmPix={async () => {
            await handleReactivationPixAction();
          }}
          serviceName={reactivationModalConfig.service}
          creditCards={credit_cards}
          isLoadingCards={is_loading_cards}
        />
      )}
      {showAiModal && (
        <ChangeAiModal
          currentProvider={jusGptData?.provider}
          onConfirm={handleChangeAiProvider}
          onClose={() => setShowAiModal(false)}
        />)}

      <UpgradePlanModal
        show={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onConfirm={handleUpgradeJusZap}
        loadPreview={() => getJusZapUpgradePreview(mainUserJusZapId, 99)}
        creditCards={credit_cards}
        isLoadingCards={is_loading_cards}
        newPlanValue={99}
      />
    </div>
  );
}

